// An opening's construction phase: numbers split by "." or "-" ("1", "1.2", "1-2", "1.2.3"). "1-2" and "1.2" are the same phase.
// Matches core/opening/phase and the phase_sort_key sql function.
const format = /^\d{1,6}([.-]\d{1,6})*$/;

export function isValidPhase(phase: string): boolean {
    const value = phase.trim();
    return value === "" || format.test(value);
}

function phaseKey(phase: string | null | undefined): number[] | null {
    const value = (phase || "").trim();
    if(!format.test(value)) return null;
    return value.split(/[.-]/).map(Number);
}

// blank or invalid phases sort last
export function comparePhase(a: string | null | undefined, b: string | null | undefined): -1 | 0 | 1 {
    const ka = phaseKey(a);
    const kb = phaseKey(b);
    if(!ka && !kb) return 0;
    if(!ka) return 1;
    if(!kb) return -1;

    for(let i = 0; i < Math.min(ka.length, kb.length); i++) {
        if(ka[i] !== kb[i]) return ka[i] < kb[i] ? -1 : 1;
    }

    return Math.sign(ka.length - kb.length) as -1 | 0 | 1;
}

export function minPhase(phases: string[]): string {
    return phases.reduce((min, p) => comparePhase(p, min) < 0 ? p : min, "");
}

export function phaseLabel(phase: string): string {
    return phaseKey(phase) ? "Phase " + phase.trim() : "No Phase";
}

// stable: items in the same phase keep their order
export function sortByPhase<T>(list: T[], getPhase: (item: T) => string): T[] {
    return list.slice().sort((a, b) => comparePhase(getPhase(a), getPhase(b)));
}

export type PhaseSection<T> = {
    phase: string;
    items: T[];
};

export function groupByPhase<T>(list: T[], getPhase: (item: T) => string): PhaseSection<T>[] {
    const sections: PhaseSection<T>[] = [];

    sortByPhase(list, getPhase).forEach(item => {
        const last = sections[sections.length - 1];
        if(last && comparePhase(last.phase, getPhase(item)) === 0) {
            last.items.push(item);
            return;
        }

        sections.push({phase: getPhase(item), items: [item]});
    });

    return sections;
}

// "1-2" and "1.2" give the same id; blank or invalid gives ""
export function phaseId(phase: string | null | undefined): string {
    const key = phaseKey(phase);
    return key ? key.join(".") : "";
}

export function phaseOptions(phases: (string | null | undefined)[]): {id: string; label: string}[] {
    return groupByPhase(phases, p => p || "").map(section => ({
        id: phaseId(section.phase),
        label: phaseLabel(section.phase || ""),
    }));
}

// an empty filter matches every phase
export function matchesPhaseFilter(phase: string | null | undefined, filter: string[]): boolean {
    return filter.length === 0 || filter.includes(phaseId(phase));
}
